import { useState } from "react";
import DOMPurify from "dompurify";
import PortalLayout from "@/components/PortalLayout";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Newspaper, Calendar, Tag, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { format } from "date-fns";
import { usePortalCompany } from "@/hooks/usePortalCompany";

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ["p", "br", "strong", "em", "b", "i", "u", "ul", "ol", "li", "a", "h2", "h3", "h4", "blockquote", "code"],
  ALLOWED_ATTR: ["href", "target", "rel"],
};

const categoryConfig: Record<string, { label: string; color: string }> = {
  operational_update: { label: "OPERATIONAL UPDATE", color: "bg-teal/15 text-teal" },
  fleet_news: { label: "FLEET NEWS", color: "bg-amber-500/15 text-amber-400" },
  newbuild_milestone: { label: "NEWBUILD MILESTONE", color: "bg-purple-500/15 text-purple-400" },
  safety_bulletin: { label: "SAFETY BULLETIN", color: "bg-destructive/15 text-destructive" },
  industry_regulatory: { label: "INDUSTRY & REGULATORY", color: "bg-primary-foreground/10 text-primary-foreground/50" },
  company_news: { label: "COMPANY NEWS", color: "bg-amber-600/15 text-amber-300" },
};

const News = () => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const { companyId } = usePortalCompany();

  const articleMatchesCompany = (article: { visibility: string; org_ids: unknown }) => {
    if (article.visibility === "public") return true;
    return !!companyId && Array.isArray(article.org_ids) && article.org_ids.includes(companyId);
  };

  const { data: articles = [], isLoading } = useQuery({
    queryKey: ["portal-news", companyId],
    queryFn: async () => {
      const { data } = await supabase
        .from("news_articles")
        .select("*")
        .eq("is_published", true)
        .order("published_at", { ascending: false });
      return (data ?? []).filter(articleMatchesCompany);
    },
    enabled: !!companyId,
  });

  const filteredArticles = filterCategory === "all"
    ? articles
    : articles.filter((a: any) => a.category === filterCategory);

  const categories = ["all", ...new Set(articles.map((a: any) => a.category))];

  return (
    <PortalLayout>
      <div className="mb-8">
        <p className="text-teal/60 text-[11px] font-bold tracking-[4px] uppercase font-body mb-2">Latest</p>
        <h1 className="font-display text-3xl text-primary-foreground tracking-tight">
          News & <span className="accent-text">Updates</span>
        </h1>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2 mb-8">
        {categories.map((cat) => {
          const cfg = cat === "all" ? { label: "ALL", color: "bg-primary-foreground/10 text-primary-foreground/50" } : (categoryConfig[cat as string] || { label: (cat as string).replace(/_/g, " ").toUpperCase(), color: "bg-primary-foreground/10 text-primary-foreground/50" });
          const active = filterCategory === cat;
          return (
            <button
              key={cat as string}
              onClick={() => setFilterCategory(cat as string)}
              className={`px-3 py-1.5 rounded-lg text-[10px] font-bold tracking-[1px] uppercase font-body transition-all ${
                active ? cfg.color + " ring-1 ring-current" : "bg-primary-foreground/5 text-primary-foreground/25 hover:text-primary-foreground/40"
              }`}
            >
              {cfg.label}
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" />
        </div>
      ) : filteredArticles.length === 0 ? (
        <div className="glass-card rounded-2xl p-16 text-center">
          <Newspaper className="w-12 h-12 text-primary-foreground/15 mx-auto mb-4" />
          <p className="text-primary-foreground/40 font-body">No updates yet — check back soon.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Hero card for first article */}
          {filteredArticles.length > 0 && expandedId !== filteredArticles[0].id && (
            <div
              className="glass-card rounded-2xl p-8 hover:border-teal/20 transition-all cursor-pointer"
              onClick={() => setExpandedId(filteredArticles[0].id)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  {(() => {
                    const cfg = categoryConfig[filteredArticles[0].category] || categoryConfig.company_news;
                    return (
                      <span className={`inline-block px-2.5 py-1 rounded text-[10px] font-bold tracking-[1px] uppercase font-body mb-4 ${cfg.color}`}>
                        {cfg.label}
                      </span>
                    );
                  })()}
                  <h2 className="font-display text-2xl text-primary-foreground mb-3">{filteredArticles[0].title}</h2>
                  <p className="text-sm text-primary-foreground/40 font-body leading-relaxed mb-4">{filteredArticles[0].excerpt}</p>
                  <div className="flex items-center gap-4 text-xs text-primary-foreground/25 font-body">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-3 h-3" />
                      {filteredArticles[0].published_at ? format(new Date(filteredArticles[0].published_at), "MMM d, yyyy") : "—"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Remaining articles */}
          {filteredArticles.slice(expandedId === filteredArticles[0]?.id ? 0 : 1).map((article: any) => {
            const expanded = expandedId === article.id;
            const cfg = categoryConfig[article.category] || categoryConfig.company_news;
            return (
              <div key={article.id} className="glass-card rounded-xl overflow-hidden">
                <button
                  onClick={() => setExpandedId(expanded ? null : article.id)}
                  className="w-full p-5 flex items-center gap-4 text-left hover:bg-primary-foreground/3 transition-all"
                >
                  <Tag className="w-4 h-4 text-teal/40 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded text-[9px] font-bold tracking-[1px] uppercase font-body ${cfg.color}`}>
                        {cfg.label}
                      </span>
                    </div>
                    <p className="text-sm text-primary-foreground/80 font-body font-medium truncate">{article.title}</p>
                    <p className="text-xs text-primary-foreground/25 font-body mt-0.5">
                      {article.published_at ? format(new Date(article.published_at), "MMM d, yyyy") : "—"}
                    </p>
                  </div>
                  {expanded ? <ChevronUp className="w-4 h-4 text-primary-foreground/30" /> : <ChevronDown className="w-4 h-4 text-primary-foreground/30" />}
                </button>
                {expanded && (
                  <div className="px-5 pb-6 border-t border-primary-foreground/5">
                    {article.excerpt && (
                      <p className="text-sm text-primary-foreground/50 font-body italic mt-4 mb-4 pl-4 border-l-2 border-teal/20">{article.excerpt}</p>
                    )}
                    {article.body_html && (
                      <div
                        className="text-sm text-primary-foreground/60 font-body leading-relaxed prose-invert prose-sm max-w-none [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.body_html, SANITIZE_CONFIG) }}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </PortalLayout>
  );
};

export default News;
